import React, { useMemo } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  LabelList,
  ResponsiveContainer,
} from "recharts";
import ChartTooltip from "./ChartTooltip";
import CurrencyFacets from "./CurrencyFacets";
import { CurrencyChip, FacetHeader, FacetWrapper } from "./layout";
import { assignCategoryColors, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_MUTED } from "./palette";
import { formatAmount, formatCompactAmount } from "./format";

const SectionHeading = styled.div`
  margin: 0.5em 0 0.25em;
  font-size: 13px;
  font-weight: 600;
  color: ${TEXT_SECONDARY};
`;

const EmptyNote = styled.div`
  color: ${TEXT_MUTED};
`;

function CategoryTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const { categoryName, total, share } = payload[0].payload;

  return <ChartTooltip label={categoryName} value={formatAmount(total)} detail={`佔 ${share}%`} />;
}

CategoryTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
};

CategoryTooltip.defaultProps = {
  active: false,
  payload: [],
};

function CurrencyCategoryChart({ currencyName, currencyId, rows, colorById, onSelect }) {
  const data = useMemo(() => {
    const currencyTotal = rows.reduce((sum, row) => sum + row.total, 0);

    return [...rows]
      .sort((a, b) => b.total - a.total)
      .map((row) => ({
        categoryId: row.category.id,
        categoryName: row.category.name,
        categoryType: row.category.type,
        total: row.total,
        share: currencyTotal > 0 ? Math.round((row.total / currencyTotal) * 100) : 0,
      }));
  }, [rows]);

  const height = Math.max(data.length * 40, 80);

  function handleClick(clickData) {
    const entry = clickData && clickData.payload ? clickData.payload : clickData;
    if (!onSelect || !entry || !entry.categoryId) {
      return;
    }
    onSelect({
      kind: "category",
      categoryId: entry.categoryId,
      categoryType: entry.categoryType,
      currencyId,
      currencyName,
      label: entry.categoryName,
    });
  }

  return (
    <FacetWrapper>
      <FacetHeader>
        <CurrencyChip>{currencyName}</CurrencyChip>
      </FacetHeader>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 72, left: 8, bottom: 4 }}>
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="categoryName"
            tick={{ fill: TEXT_PRIMARY, fontSize: 13 }}
            axisLine={false}
            tickLine={false}
            width={90}
          />
          <Tooltip content={<CategoryTooltip />} cursor={{ fill: "rgba(11,11,11,0.04)" }} />
          <Bar
            dataKey="total"
            radius={[0, 4, 4, 0]}
            maxBarSize={24}
            onClick={handleClick}
            cursor={onSelect ? "pointer" : "default"}
          >
            {data.map((entry) => (
              <Cell key={entry.categoryId} fill={colorById[entry.categoryId]} />
            ))}
            <LabelList
              dataKey={(entry) => `${formatCompactAmount(entry.total)} (${entry.share}%)`}
              position="right"
              style={{ fill: TEXT_PRIMARY, fontSize: 12 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </FacetWrapper>
  );
}

CurrencyCategoryChart.propTypes = {
  currencyName: PropTypes.string.isRequired,
  currencyId: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  colorById: PropTypes.objectOf(PropTypes.string).isRequired,
  onSelect: PropTypes.func,
};

CurrencyCategoryChart.defaultProps = {
  onSelect: null,
};

function BreakdownSection({ heading, rows, colorById, onSelect }) {
  if (!rows || rows.length === 0) {
    return null;
  }

  return (
    <>
      <SectionHeading>{heading}</SectionHeading>
      <CurrencyFacets rows={rows}>
        {(currency, currencyRows) => (
          <CurrencyCategoryChart
            key={currency.id}
            currencyName={currency.name}
            currencyId={currency.id}
            rows={currencyRows}
            colorById={colorById}
            onSelect={onSelect}
          />
        )}
      </CurrencyFacets>
    </>
  );
}

BreakdownSection.propTypes = {
  heading: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  colorById: PropTypes.objectOf(PropTypes.string).isRequired,
  onSelect: PropTypes.func,
};

BreakdownSection.defaultProps = {
  onSelect: null,
};

export default function CategoryBreakdownChart({ categorySpending, onSelect }) {
  // 顏色跟著類別走：以「全部」類別（跨幣別、跨收支）一次指定，
  // 同一類別在不同幣別的圖中才會是同一個顏色。
  const colorById = useMemo(
    () => assignCategoryColors((categorySpending || []).map((row) => row.category.id)),
    [categorySpending]
  );

  const rows = categorySpending || [];
  const expenseRows = rows.filter((row) => row.category.type !== "income");
  const incomeRows = rows.filter((row) => row.category.type === "income");

  if (rows.length === 0) {
    return <EmptyNote>目前沒有收支資料可供分析。</EmptyNote>;
  }

  return (
    <>
      <BreakdownSection
        heading="支出"
        rows={expenseRows}
        colorById={colorById}
        onSelect={onSelect}
      />
      <BreakdownSection
        heading="收入"
        rows={incomeRows}
        colorById={colorById}
        onSelect={onSelect}
      />
    </>
  );
}

CategoryBreakdownChart.propTypes = {
  categorySpending: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func,
};

CategoryBreakdownChart.defaultProps = {
  categorySpending: null,
  onSelect: null,
};
